import React, { useState, useEffect } from 'react';
import './CreateSupplierModal.css';
import { FaTimes, FaPlus, FaBuilding, FaExclamationTriangle, FaEdit, FaInfoCircle } from 'react-icons/fa';

/**
 * CreateSupplierModal Component
 * 
 * Provides a dialog to register new medicine/pharmacy supplier details.
 * Implements strict input validation preventing forbidden characters (comma, single quotes, double quotes)
 * on specified fields according to system requirements:
 * - Name
 * - Address
 * - Phone1
 * - Phone2
 * - Email
 * - GST Number
 * - DL1 Number
 * - DL2 Number
 * 
 * In addition to the restricted fields, it includes a 'Description' field.
 * 
 * @param {boolean} isOpen - Controls visibility of the modal dialog
 * @param {function} onClose - Callback triggered to close/dismiss the modal
 * @param {function} onSave - Callback receiving the created or updated supplier details object
 * @param {object|null} supplierToEdit - Optional existing supplier object when in edit mode
 */
// ─── Initial Form State with User-Specified Supplier Fields ──────────────
const INITIAL_SUPPLIER_FORM_STATE = {
  name: '',          // Supplier Name (restricted chars)
  description: '',   // Description (unrestricted)
  address: '',       // Address (restricted chars)
  phone1: '',        // Phone1 (restricted chars)
  phone2: '',        // Phone2 (restricted chars)
  email: '',         // Email (restricted chars)
  gstNumber: '',     // GST Number (restricted chars)
  dl1Number: '',     // DL1 Number (Drug License 1, restricted chars)
  dl2Number: '',     // DL2 Number (Drug License 2, restricted chars)
  status: 'ACTIVE'   // Supplier status (ACTIVE / INACTIVE)
};

const CreateSupplierModal = ({ isOpen, onClose, onSave, supplierToEdit = null }) => {
  const [formData, setFormData] = useState(INITIAL_SUPPLIER_FORM_STATE);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Track specific fields that currently have validation warnings
  const [fieldErrors, setFieldErrors] = useState({});

  // Populate form with existing supplier details if editing, or reset to blank if creating new
  useEffect(() => {
    if (supplierToEdit) {
      setFormData({
        name: supplierToEdit.name || '',
        description: supplierToEdit.description || '',
        address: supplierToEdit.address || '',
        phone1: supplierToEdit.phone1 || '',
        phone2: supplierToEdit.phone2 || '',
        email: supplierToEdit.email || '',
        gstNumber: supplierToEdit.gstNumber || '',
        dl1Number: supplierToEdit.dl1Number || '',
        dl2Number: supplierToEdit.dl2Number || '',
        status: supplierToEdit.status || 'ACTIVE'
      });
    } else {
      setFormData(INITIAL_SUPPLIER_FORM_STATE);
    }
    setFieldErrors({});
    setErrorMessage('');
  }, [supplierToEdit, isOpen]);

  // Regular expression detecting forbidden special characters: comma (,), double quote ("), and single quote (')
  const FORBIDDEN_REGEX = /[,'""]/;

  // List of fields where special characters (comma, double quotes, single quotes) are strictly forbidden
  const RESTRICTED_FIELDS = ['name', 'address', 'phone1', 'phone2', 'email', 'gstNumber', 'dl1Number', 'dl2Number'];

  // Do not render anything if the modal is hidden
  if (!isOpen) return null;

  /**
   * Validates if a string contains any of the prohibited characters (comma, single quote, double quote)
   * @param {string} text - Input text to test
   * @returns {boolean} True if text contains prohibited characters
   */
  const containsForbiddenChar = (text) => {
    return FORBIDDEN_REGEX.test(text);
  };

  /**
   * Handles input changes with real-time character validation
   * Prohibits typing comma, single quote, or double quote on restricted fields
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>} e - Change event
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Check if the current field is in the list of restricted fields
    if (RESTRICTED_FIELDS.includes(name)) {
      if (containsForbiddenChar(value)) {
        // Flag error for this field
        setFieldErrors((prev) => ({
          ...prev,
          [name]: "Comma, single quotes and double quotes are not allowed in this field."
        }));

        // Sanitize input by stripping out the forbidden characters (comma, double quotes, single quotes)
        const sanitizedValue = value.replace(/[, '"]/g, (match) => {
          // Keep normal spaces, strip out commas, single quotes, double quotes
          return match === ' ' ? ' ' : '';
        });

        setFormData((prev) => ({
          ...prev,
          [name]: sanitizedValue
        }));
        return;
      } else {
        // Clear field error if value is clean
        if (fieldErrors[name]) {
          setFieldErrors((prev) => {
            const updated = { ...prev };
            delete updated[name];
            return updated;
          });
        }
      }
    }

    // Update form field state
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  /**
   * Toggles supplier active status
   * @param {'ACTIVE' | 'INACTIVE'} statusValue - Status selection
   */
  const handleStatusChange = (statusValue) => {
    setFormData((prev) => ({
      ...prev,
      status: statusValue
    }));
  };

  /**
   * Validates all fields and triggers supplier save
   * @param {React.FormEvent} e - Form submission event
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    // 1. Validate mandatory fields
    if (!formData.name.trim()) {
      setErrorMessage("Please enter the supplier Name.");
      return;
    }
    if (!formData.address.trim()) {
      setErrorMessage("Please enter the supplier Address.");
      return;
    }
    if (!formData.phone1.trim()) {
      setErrorMessage("Please enter Phone1.");
      return;
    }

    // 2. Comprehensive check across all restricted fields for forbidden characters
    for (const field of RESTRICTED_FIELDS) {
      const val = formData[field] || '';
      if (containsForbiddenChar(val)) {
        setErrorMessage(`Invalid characters detected in ${field}: Comma, single quotes and double quotes are not allowed.`);
        setFieldErrors((prev) => ({
          ...prev,
          [field]: "Special characters comma, double quotes and single quotes are not allowed."
        }));
        return;
      }
    }

    // 3. Construct supplier details object (preserving existing ID if editing)
    const savedSupplier = {
      id: supplierToEdit ? supplierToEdit.id : Date.now(),
      name: formData.name.trim(),
      description: formData.description.trim(),
      address: formData.address.trim(),
      phone1: formData.phone1.trim(),
      phone2: formData.phone2.trim(),
      email: formData.email.trim(),
      gstNumber: formData.gstNumber.trim(),
      dl1Number: formData.dl1Number.trim(),
      dl2Number: formData.dl2Number.trim(),
      status: formData.status
    };

    // Save and close modal
    onSave(savedSupplier);
    setFormData(INITIAL_SUPPLIER_FORM_STATE);
    setFieldErrors({});
    setErrorMessage('');
    onClose();
  };

  return (
    <div className="supplier-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      {/* Modal card container with click propagation stopped */}
      <div className="supplier-modal-container" onClick={(e) => e.stopPropagation()}>
        
        {/* ─── Modal Header ─────────────────────────────────────────────── */}
        <div className="supplier-modal-header">
          <h3 className="supplier-modal-title">
            <FaBuilding className="supplier-modal-title-icon" />
            {supplierToEdit ? 'Edit Supplier Details' : 'Create New Supplier Details'}
          </h3>
          <button
            type="button"
            className="supplier-modal-close-btn"
            onClick={onClose}
            title="Close modal"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* ─── Modal Form Body ─────────────────────────────────────────── */}
        <form onSubmit={handleSubmit} className="supplier-modal-body">
          
          {/* General error message banner */}
          {errorMessage && (
            <div style={{
              backgroundColor: '#fee2e2',
              color: '#b91c1c',
              padding: '10px 14px',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '0.88rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FaExclamationTriangle />
              {errorMessage}
            </div>
          )}

          {/* Clean Guidance Banner */}
          <div className="supplier-modal-info-banner">
            <FaInfoCircle className="info-banner-icon" />
            <span>
              <strong>Special Characters Restricted:</strong> Comma (<code>,</code>), single quotes (<code>'</code>), and double quotes (<code>"</code>) are forbidden on marked fields.
            </span>
          </div>

          <div className="supplier-form-grid">
            
            {/* 1. Name */}
            <div className="supplier-form-group">
              <label className="supplier-form-label">
                Name <span className="required-star">*</span>
              </label>
              <input
                type="text"
                name="name"
                placeholder="Enter supplier name"
                value={formData.name}
                onChange={handleInputChange}
                className={`supplier-form-input ${fieldErrors.name ? 'input-error' : ''}`}
                required
              />
              {fieldErrors.name && (
                <span className="supplier-field-note error-state">{fieldErrors.name}</span>
              )}
            </div>

            {/* 2. Description */}
            <div className="supplier-form-group">
              <label className="supplier-form-label">Description</label>
              <input
                type="text"
                name="description"
                placeholder="Enter supplier description"
                value={formData.description}
                onChange={handleInputChange}
                className="supplier-form-input"
              />
            </div>

            {/* 3. Address */}
            <div className="supplier-form-group supplier-form-full-width">
              <label className="supplier-form-label">
                Address <span className="required-star">*</span>
              </label>
              <input
                type="text"
                name="address"
                placeholder="Enter street, city, state address"
                value={formData.address}
                onChange={handleInputChange}
                className={`supplier-form-input ${fieldErrors.address ? 'input-error' : ''}`}
                required
              />
              {fieldErrors.address && (
                <span className="supplier-field-note error-state">{fieldErrors.address}</span>
              )}
            </div>

            {/* 4. Phone1 */}
            <div className="supplier-form-group">
              <label className="supplier-form-label">
                Phone 1 <span className="required-star">*</span>
              </label>
              <input
                type="tel"
                name="phone1"
                placeholder="Enter primary phone number"
                value={formData.phone1}
                onChange={handleInputChange}
                className={`supplier-form-input ${fieldErrors.phone1 ? 'input-error' : ''}`}
                required
              />
              {fieldErrors.phone1 && (
                <span className="supplier-field-note error-state">{fieldErrors.phone1}</span>
              )}
            </div>

            {/* 5. Phone2 */}
            <div className="supplier-form-group">
              <label className="supplier-form-label">Phone 2</label>
              <input
                type="tel"
                name="phone2"
                placeholder="Enter secondary phone number"
                value={formData.phone2}
                onChange={handleInputChange}
                className={`supplier-form-input ${fieldErrors.phone2 ? 'input-error' : ''}`}
              />
              {fieldErrors.phone2 && (
                <span className="supplier-field-note error-state">{fieldErrors.phone2}</span>
              )}
            </div>

            {/* 6. Email */}
            <div className="supplier-form-group">
              <label className="supplier-form-label">Email</label>
              <input
                type="email"
                name="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={handleInputChange}
                className={`supplier-form-input ${fieldErrors.email ? 'input-error' : ''}`}
              />
              {fieldErrors.email && (
                <span className="supplier-field-note error-state">{fieldErrors.email}</span>
              )}
            </div>

            {/* 7. GST Number */}
            <div className="supplier-form-group">
              <label className="supplier-form-label">GST Number</label>
              <input
                type="text"
                name="gstNumber"
                placeholder="Enter GST number"
                value={formData.gstNumber}
                onChange={handleInputChange}
                className={`supplier-form-input ${fieldErrors.gstNumber ? 'input-error' : ''}`}
              />
              {fieldErrors.gstNumber && (
                <span className="supplier-field-note error-state">{fieldErrors.gstNumber}</span>
              )}
            </div>

            {/* 8. DL1 Number */}
            <div className="supplier-form-group">
              <label className="supplier-form-label">DL1 Number</label>
              <input
                type="text"
                name="dl1Number"
                placeholder="Enter Drug License 1 number"
                value={formData.dl1Number}
                onChange={handleInputChange}
                className={`supplier-form-input ${fieldErrors.dl1Number ? 'input-error' : ''}`}
              />
              {fieldErrors.dl1Number && (
                <span className="supplier-field-note error-state">{fieldErrors.dl1Number}</span>
              )}
            </div>

            {/* 9. DL2 Number */}
            <div className="supplier-form-group">
              <label className="supplier-form-label">DL2 Number</label>
              <input
                type="text"
                name="dl2Number"
                placeholder="Enter Drug License 2 number"
                value={formData.dl2Number}
                onChange={handleInputChange}
                className={`supplier-form-input ${fieldErrors.dl2Number ? 'input-error' : ''}`}
              />
              {fieldErrors.dl2Number && (
                <span className="supplier-field-note error-state">{fieldErrors.dl2Number}</span>
              )}
            </div>

            {/* Status (Active: Green / Inactive: Red) */}
            <div className="supplier-form-group">
              <label className="supplier-form-label">Supplier Status</label>
              <div className="supplier-status-toggle-wrapper">
                <div
                  className={`supplier-status-option status-opt-active ${formData.status === 'ACTIVE' ? 'active-option-green' : ''}`}
                  onClick={() => handleStatusChange('ACTIVE')}
                >
                  <input
                    type="radio"
                    name="status"
                    checked={formData.status === 'ACTIVE'}
                    onChange={() => handleStatusChange('ACTIVE')}
                  />
                  <span className="status-label-with-icon">
                    <span className="status-indicator-dot dot-green" />
                    Active
                  </span>
                </div>
                <div
                  className={`supplier-status-option status-opt-inactive ${formData.status === 'INACTIVE' ? 'active-option-red' : ''}`}
                  onClick={() => handleStatusChange('INACTIVE')}
                >
                  <input
                    type="radio"
                    name="status"
                    checked={formData.status === 'INACTIVE'}
                    onChange={() => handleStatusChange('INACTIVE')}
                  />
                  <span className="status-label-with-icon">
                    <span className="status-indicator-dot dot-red" />
                    Inactive
                  </span>
                </div>
              </div>
            </div>

          </div>

          {/* ─── Modal Action Buttons ──────────────────────────────────────── */}
          <div className="supplier-modal-footer">
            <button
              type="button"
              className="supplier-btn-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="supplier-btn-submit"
            >
              {supplierToEdit ? (
                <>
                  <FaEdit /> Update Supplier Details
                </>
              ) : (
                <>
                  <FaPlus /> Save Supplier Details
                </>
              )}
            </button>
          </div>

        </form>

      </div>
    </div>
  );
};

export default CreateSupplierModal;
